export function formatUsd(n: number): string {
  const sign = n < 0 ? "−" : "";
  return `${sign}$${Math.abs(n).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })}`;
}

// Per-stop palette for the trailer SVG + manifest "Stop" cell. Distinct
// from the route polyline colors (which mark which truck a pallet rides)
// so an operator never confuses stop-color with truck-color.
export const STOP_PALETTE = [
  "#E84855", "#0E7C7B", "#F18F01", "#9D4EDD", "#1B998B",
  "#C9266B", "#177E89", "#F4B400", "#5D2E8C", "#FF6B35",
  "#2EC4B6", "#A23E48", "#8338EC", "#3A0CA3", "#06A77D",
  "#D62246", "#0CCE6B", "#B91372", "#118AB2", "#EF233C",
];

// One color per distinct stop number, in delivery order.
export function stopColorMap(stopNumbers: (number | null)[]): Map<number, string> {
  const map = new Map<number, string>();
  const distinct = [...new Set(stopNumbers.filter((n): n is number => n !== null))]
    .sort((a, b) => a - b);
  distinct.forEach((n, i) => map.set(n, STOP_PALETTE[i % STOP_PALETTE.length]));
  return map;
}

// Minutes after midnight as a clock time, e.g. 545 → "9:05 AM".
export function formatClock(min: number | null | undefined): string {
  if (min === null || min === undefined) return "—";
  const h24 = Math.floor(min / 60) % 24;
  const m = String(min % 60).padStart(2, "0");
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${m} ${h24 < 12 ? "AM" : "PM"}`;
}

// A provider timestamp as a clock time at the depot, e.g. "9:02 AM".
export function clockAt(iso: string | null, timeZone: string): string {
  if (!iso) return "—";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return formatClock(part("hour") * 60 + part("minute"));
}

// A time zone's short name at a moment, e.g. "PDT" for Long Beach in October.
export function zoneName(iso: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" }).formatToParts(
    new Date(iso),
  );
  return parts.find((p) => p.type === "timeZoneName")?.value ?? timeZone;
}

// A timestamp as a clock time at the depot, dated when it falls on another day
// than `day` (YYYY-MM-DD), e.g. "Oct 3, 10:00 PM" for a proof sent a day late.
export function clockOnDay(iso: string | null, timeZone: string, day: string): string {
  if (!iso) return "—";
  const at = new Date(iso);
  const local = new Intl.DateTimeFormat("en-CA", { timeZone }).format(at); // YYYY-MM-DD
  if (local === day) return clockAt(iso, timeZone);
  const date = new Intl.DateTimeFormat("en-US", { timeZone, month: "short", day: "numeric" });
  return `${date.format(at)}, ${clockAt(iso, timeZone)}`;
}

// Today's date (YYYY-MM-DD) in a time zone, e.g. at the depot.
export function todayIn(timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone }).format(new Date());
}

// A clock time ("HH:MM") on `day` (YYYY-MM-DD) at a time zone, as a UTC
// instant, e.g. 11:00 on 2026-10-01 in Long Beach is "2026-10-01T18:00:00.000Z".
export function instantOnDay(day: string, hhmm: string, timeZone: string): string {
  const [y, mo, d] = day.split("-").map(Number);
  const [h, mi] = hhmm.split(":").map(Number);
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  // The zone's clock at that instant, read as if it were UTC, is ahead of it
  // by the zone's offset.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h23",
  }).formatToParts(new Date(guess));
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const wall = Date.UTC(part("year"), part("month") - 1, part("day"), part("hour"), part("minute"));
  return new Date(guess - (wall - guess)).toISOString();
}
